/**
 * Fills and submits a newsletter form straight from a link.
 *
 * The order confirmation email links here with the shopper's address and
 * `newsletter_autosignup=1`. They already clicked "Sign Up & Get $5 OFF" in the
 * email, so asking them to retype an address we were given and press a second
 * button loses people for no reason: fill it, post it, let the form show its own
 * success notice. Without `newsletter_autosignup` the address is only filled in,
 * which is what a plain `?email=` link should do.
 *
 * Liquid cannot read query parameters, so both steps have to happen here.
 *
 * Targets `contact[email]` rather than a class, so it covers every Shopify
 * customer form in the theme (the shared snippet and the blog block use
 * different markup) and any new one without another change to this file.
 *
 * Only ever fills a field that is already empty, so a logged-in customer's own
 * address, or anything the shopper has started typing, always wins.
 */
(function () {
  if (window.__altenewNewsletterAutosignup) return;
  window.__altenewNewsletterAutosignup = true;

  var SELECTOR = 'input[type="email"][name="contact[email]"]';
  var AUTO_PARAM = 'newsletter_autosignup';
  var PENDING_KEY = 'altenew:autosignup:pending';

  // Shopify drops the original query string when it redirects back from
  // /contact, so a posted page can be recognised by this alone.
  var POSTED = /(^|[?&])customer_posted=true/.test(window.location.search);

  function query() {
    try {
      return new URLSearchParams(window.location.search);
    } catch (e) {
      return null;
    }
  }

  function store() {
    // Safari in private mode throws on access rather than returning null.
    try {
      var s = window.sessionStorage;
      s.getItem(PENDING_KEY);
      return s;
    } catch (e) {
      return null;
    }
  }

  function emailFrom(params) {
    var raw = params.get('email');
    if (!raw) return null;

    var email = raw.trim();
    // The value arrives from a URL, so treat it as untrusted: only a plausible
    // single address gets anywhere near the field.
    if (email.length > 254) return null;
    if (!/^[^\s@,;<>"']+@[^\s@,;<>"']+\.[^\s@,;<>"']+$/.test(email)) return null;
    return email;
  }

  function fill(email, root) {
    var filled = null;
    (root || document).querySelectorAll(SELECTOR).forEach(function (input) {
      if (input.value) return;
      input.value = email;
      // Let any framework or app listening on the field know it changed.
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      if (!filled) filled = input;
    });
    return filled;
  }

  /**
   * Brings the shopper to a filled-in form ready to send.
   *
   * Posting it for them is not on the table: this store runs Shopify's CAPTCHA
   * on customer forms, and the token is only minted by a genuine user submit.
   * A scripted submit posts without one and comes straight back as "Missing
   * CAPTCHA token", and a scripted click on the button is ignored outright
   * because the event is not trusted. Getting past that would mean defeating
   * the store's own bot protection, so instead the form is put in front of
   * them, filled, with the button focused — one deliberate tap to finish.
   */
  function presentForm(email, input) {
    var form = input && input.form;
    if (!form) return false;

    // A band that carries a Klaviyo embed keeps this form hidden behind the
    // provider guard until it knows which of the two to show. Scrolling to
    // something off screen and focusing a button nobody can see would only
    // move the page away from the form the shopper is actually going to use,
    // and newsletter-autosignup-klaviyo.js takes that band from here.
    if (!form.offsetParent) return false;

    var session = store();
    if (session) session.setItem(PENDING_KEY, email);

    var button = form.querySelector('button[type="submit"], input[type="submit"]');

    try {
      form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (e) {
      form.scrollIntoView();
    }

    // Focus the button rather than the field: the address is already in, so
    // the next thing they want is to send it, and this makes that a single
    // keypress for anyone on a keyboard or screen reader.
    if (button) {
      try {
        button.focus({ preventScroll: true });
      } catch (e) {
        button.focus();
      }
    }

    return true;
  }

  /**
   * The welcome offer is issued once per subscriber, and Shopify's customer
   * form returns the same success whether the address was new or already on the
   * list. Rather than claim something we cannot check, say plainly that it is a
   * one-time offer, and only to people who arrived by clicking the email.
   */
  function noteOneTimeOffer() {
    var session = store();
    if (!session || !session.getItem(PENDING_KEY)) return;
    session.removeItem(PENDING_KEY);

    var anchor = document.querySelector(
      '.note--success, .cbl-subscribe__msg, [role="status"]'
    );
    if (!anchor || document.querySelector('[data-autosignup-note]')) return;

    var note = document.createElement('p');
    note.setAttribute('data-autosignup-note', '');
    note.style.cssText = 'margin:8px 0 0;font-size:13px;line-height:19px;opacity:.75;';
    note.textContent =
      'Already on our list? No need to sign up again — the welcome offer can only be used once.';
    anchor.insertAdjacentElement('afterend', note);
  }

  function start() {
    if (POSTED) {
      noteOneTimeOffer();
      return;
    }

    var params = query();
    if (!params) return;

    var email = emailFrom(params);
    if (!email) return;

    var auto = params.get(AUTO_PARAM) === '1';
    var presented = false;

    function pass() {
      var input = fill(email) || document.querySelector(SELECTOR);
      if (auto && !presented) presented = presentForm(email, input);
    }

    pass();

    // Some forms (Klaviyo embeds, app blocks) mount after this runs, so keep
    // watching briefly rather than filling once and giving up. This used to
    // stop after the first pass on the auto path, which is exactly the path
    // that needs the wait most: a band behind the provider guard has no form
    // on screen yet when the first pass runs.
    if (!('MutationObserver' in window)) return;
    var observer = new MutationObserver(pass);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(function () {
      observer.disconnect();
    }, 10000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
